import { z } from 'zod';
import { ControlDomain } from './controls.js';
import { ProfileReview } from './review-steps.js';
/**
 * Industry profiles (tracker task 79, decision 0058): how an industry is assessed and scored. One profile
 * per industry, keyed by the industry (`manufacturing`, `healthcare`, …). The platform publishes one for
 * each, and an insurer can customise its own copy (its appetite: a stricter minimum band, say).
 *
 * A profile sets:
 * - the journey the industry follows when several are published;
 * - how deep it goes: the share of applicable controls to answer for a reliable score (task 86), deeper
 *   for larger businesses;
 * - the inherent-risk multiplier;
 * - domain weights, replacing the model's where the industry's losses come from elsewhere (patient
 *   records weigh more in healthcare, privileged access in financial services);
 * - kill switches of its own: pack controls that cap the score in this industry, or a different cap for
 *   a common one;
 * - the minimum band the insurer's appetite accepts. Below it, the assessment is referred;
 * - review steps before cover is decided (task 80, `review-steps.ts`).
 */
export const PROFILE_DEPTHS = {
    light: { label: 'Light', coverage: 0.7, help: '70% of what applies answered' },
    standard: { label: 'Standard', coverage: 0.8, help: '80% of what applies answered' },
    thorough: { label: 'Thorough', coverage: 0.9, help: '90% of what applies answered' },
};
// Spelled out: scoring reads profiles, so profiles can't read scoring's bands at load time.
const BAND_KEYS = ['POOR', 'FAIR', 'GOOD', 'VERY_GOOD', 'EXCELLENT'];
export const ProfileKillSwitch = z.strictObject({
    control: z.string().min(1),
    /** What failing it caps the score at. */
    cap: z.number().int().min(300).max(849),
    severity: z.enum(['critical', 'high', 'medium']),
});
export const IndustryProfileDefinition = z.strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    /** The journey an assessment for this industry follows when several are published. */
    journey: z.string().min(1).optional(),
    /** How much must be answered for a reliable score. Unset: each journey's own target. */
    depth: z.enum(Object.keys(PROFILE_DEPTHS)).optional(),
    /** Larger businesses go deeper: from this many staff, this depth instead. */
    larger: z
        .strictObject({
        staff: z.number().int().min(2).max(100_000),
        depth: z.enum(Object.keys(PROFILE_DEPTHS)),
    })
        .optional(),
    /** Inherent risk multiplier. Unset: the model's for the industry. */
    multiplier: z.number().min(0.5).max(3).optional(),
    /** Domain weights replacing the model's (relative: they are scaled to the domains that apply). */
    domain_weights: z.partialRecord(ControlDomain, z.number().int().min(1).max(40)).default({}),
    kill_switches: z.array(ProfileKillSwitch).max(30).default([]),
    /** The lowest band the insurer's appetite accepts; below it the assessment is referred. */
    minimum_band: z.enum(BAND_KEYS).optional(),
    /** Steps before cover is decided (task 80): a specialist review, evidence, a second sign-off. */
    review: ProfileReview.optional(),
});
/** The coverage target a profile sets, or undefined when it leaves it to the journey. */
export const profileCoverage = (p) => p?.depth ? PROFILE_DEPTHS[p.depth].coverage : undefined;
/**
 * The coverage target for a business: the larger-business depth once its staff count reaches it, else the
 * journey's own target (which the bundle already set from the profile's depth).
 */
export function coverageTarget(profile, journeyCoverage, staff, fallback) {
    const own = journeyCoverage ?? fallback;
    const big = profile?.larger && typeof staff === 'number' && staff >= profile.larger.staff;
    return big ? Math.max(own, PROFILE_DEPTHS[profile.larger.depth].coverage) : own;
}
const BAND_WORDS = { POOR: 'Poor', FAIR: 'Fair', GOOD: 'Good', VERY_GOOD: 'Very good', EXCELLENT: 'Excellent' }; // prettier-ignore
/**
 * What a profile changes from the CyberScore model, in plain words (task 113, decision 0074), so an admin
 * sees at a glance whether it runs on the recommended settings and, if not, what differs. Empty: it uses
 * the model's settings throughout. The journey, name and description are not settings of the model.
 */
export function profileChanges(p, modelMultiplier) {
    const out = [];
    if (p.depth)
        out.push(`Depth: ${PROFILE_DEPTHS[p.depth].label.toLowerCase()} (${PROFILE_DEPTHS[p.depth].help})`);
    if (p.larger)
        out.push(`From ${p.larger.staff} staff: ${PROFILE_DEPTHS[p.larger.depth].label.toLowerCase()} depth`);
    if (p.multiplier !== undefined && p.multiplier !== modelMultiplier)
        out.push(`Inherent risk multiplier ${p.multiplier}`);
    const w = Object.keys(p.domain_weights ?? {}).length;
    if (w)
        out.push(`${w} domain ${w === 1 ? 'weight' : 'weights'} of its own`);
    const k = p.kill_switches?.length ?? 0;
    if (k)
        out.push(`${k} kill ${k === 1 ? 'switch' : 'switches'} of its own`);
    if (p.minimum_band)
        out.push(`Minimum band: ${BAND_WORDS[p.minimum_band]}`);
    if ((p.review?.sign_offs ?? 1) > 1)
        out.push('Two underwriters sign off');
    if (p.review?.evidence_before_accept?.length)
        out.push(`Evidence before accepting: ${p.review.evidence_before_accept.join(', ')}`); // prettier-ignore
    if (p.review?.specialist)
        out.push(`Specialist review: ${p.review.specialist.label}`);
    return out;
}
/** Is `band` below the profile's minimum? */
export function belowMinimum(band, minimum) {
    return !!minimum && BAND_KEYS.indexOf(band) < BAND_KEYS.indexOf(minimum);
}
/**
 * A bundle's items with the profile of its industry applied, so every engine reads the same thing:
 * - its kill switches are kill switches on their controls, and on the questions that ask about them (the
 *   ranking puts them first, readiness waits for them);
 * - its depth is every journey's coverage target.
 * Domain weights, caps, the multiplier and the minimum band are read by scoring from the profile itself.
 * Without a profile for the industry, the items come back unchanged, so bundles hash as before.
 */
export function applyIndustryProfile(items, industry) {
    const parsed = industry ? IndustryProfileDefinition.safeParse(items.industry_profile?.[industry]) : null;
    if (!parsed?.success)
        return items;
    const p = parsed.data;
    const ks = new Set(p.kill_switches.map((k) => k.control));
    const coverage = profileCoverage(p);
    const out = { ...items };
    if (ks.size) {
        out.control = Object.fromEntries(Object.entries(items.control ?? {}).map(([k, b]) => [
            k,
            ks.has(k) ? { ...b, kill_switch: true } : b,
        ]));
        out.question = Object.fromEntries(Object.entries(items.question ?? {}).map(([k, b]) => {
            const m = (b.metadata ?? {});
            const asks = !m.probe_of && (m.controls ?? []).some((c) => ks.has(c));
            return [k, asks ? { ...b, metadata: { ...m, kill_switch: true } } : b];
        }));
    }
    if (coverage !== undefined)
        out.journey = Object.fromEntries(Object.entries(items.journey ?? {}).map(([k, b]) => [k, { ...b, coverage }]));
    return out;
}
//# sourceMappingURL=profiles.js.map